import { useMemo } from 'react';
import { PlusIcon, RotateCcwIcon, SendIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { Toggle } from '../ui/Toggle';
import { Badge } from '../ui/Badge';
import { CheckList } from '../ui/CheckList';
import { NumberInput } from '../ui/NumberInput';
import { NewsletterPreview } from '../newsletter/NewsletterPreview';
import { mailPreviewHtml } from './mailHtml';
import { btnGhost, btnIcon, btnSecondary, inputCls } from '../ui/styles';
import { MAIL_PREHEADER_MAX, MAIL_SUBJECT_MAX, isStandardText, mailChecks, seriesChecks, seriesWaitLimits, withStandardText } from '../../core/rules/mail';
import type { MailFrameContext } from '../newsletter/newsletterHtml';
import type { MailSeries, MailTemplate, MailTextBlock } from '../../core/types/mail';

/** Wat Sarah aan een mail of reeks kan aanpassen. */
export type MailPatch = Partial<Pick<MailTemplate, 'subject' | 'preheader' | 'blocks' | 'enabled' | 'button'>> & {
  wait?: number;
};
type Props = {
  item: MailTemplate | MailSeries;
  onChange: (patch: MailPatch) => void;
  onTest: () => void;
  frame: MailFrameContext;
  from: string;
  /** Niet-opgeslagen wijzigingen: dan kan de testmail nog niet (de server stuurt de opgeslagen tekst). */
  dirty: boolean;
};
const isSeries = (m: MailTemplate | MailSeries): m is MailSeries => 'wait' in m;

/** Eén automatische mail of reeks: tekst, invulvelden, aan/uit en het voorbeeld in de huisstijl. */
export function MailTextEditor({
  item,
  onChange,
  onTest,
  frame,
  from,
  dirty
}: Props) {
  const series = isSeries(item);
  const required = !series && item.required;
  const preview = useMemo(() => mailPreviewHtml(item, frame), [item, frame]);
  const checks = series ? seriesChecks(item) : mailChecks(item);
  const standard = isStandardText(item);
  const setBlock = (i: number, patch: Partial<MailTextBlock>) => onChange({
    blocks: item.blocks.map((b, j) => j === i ? {
      ...b,
      ...patch
    } : b)
  });
  const addBlock = () => {
    const taken = new Set(item.blocks.map(b => b.id));
    let n = item.blocks.length + 1;
    while (taken.has(`b${n}`)) n++;
    onChange({
      blocks: [...item.blocks, {
        id: `b${n}`,
        heading: '',
        body: ''
      }]
    });
  };
  const copyVariable = async (key: string) => {
    try {
      await navigator.clipboard.writeText(`{${key}}`);
      toast.success(`{${key}} gekopieerd`, {
        description: 'Plak het in het onderwerp of de tekst.'
      });
    } catch {
      toast(`Typ {${key}} in de tekst`, {
        description: 'Kopiëren lukte niet in deze browser.'
      });
    }
  };
  return <div className="grid gap-6 lg:grid-cols-12" data-id="element-2704" data-mp="uoh6nf673nop">
      <div className="min-w-0 space-y-6 lg:col-span-7" data-id="element-2705" data-mp="un623d4xebwj">
        <Panel title={item.name} description={item.trigger} actions={required ? <Badge tone="neutral" data-id="element-2707" data-mp="3fef1hsybiuwt">Verplicht</Badge> : standard ? <Badge tone="neutral" data-id="element-2708" data-mp="3ffztpbx5u1ri">Standaardtekst</Badge> : <Badge tone="soft" data-id="element-2709" data-mp="3afva3d0ygqx2">Eigen tekst</Badge>} data-id="element-2706" data-mp="ppmp0gjys566">
          <div className="space-y-5" data-id="element-2710" data-mp="3aeagpqqdne0s">
            {required ? <p className="text-[13px] leading-relaxed text-muted" data-id="element-2711" data-mp="30cpysloqwllf">
                Deze mail is verplicht (bevestiging, betaling of inloggen) en staat altijd aan. Je kunt de tekst wel aanpassen.
              </p> : <Toggle checked={item.enabled} onChange={enabled => onChange({
            enabled
          })} label={item.enabled ? 'Staat aan' : 'Staat uit'} description={series ? item.audience : 'Uit = deze mail gaat niet meer de deur uit.'} data-id="element-2712" data-mp="3a8jhynwyzdc6" />}
            {series && <Field label={item.waitUnit === 'uren' ? 'Wachttijd in uren' : 'Wachttijd in dagen'} hint={`${item.waitUnit === 'dagen' ? '0 = direct. ' : ''}Tussen ${seriesWaitLimits(item).min} en ${seriesWaitLimits(item).max}.`} data-id="element-2713" data-mp="3szsxpexc2yc7">
                <NumberInput value={item.wait} onChange={wait => onChange({
              wait: wait ?? 0
            })} min={seriesWaitLimits(item).min} max={seriesWaitLimits(item).max} className="sm:max-w-[140px]" data-id="element-2714" data-mp="23lecv5pfrvlz" />
              </Field>}
          </div>
        </Panel>

        <Panel title="In de inbox" data-id="element-2715" data-mp="3p2wvqjfdonl5">
          <div className="space-y-5" data-id="element-2716" data-mp="6xtmqnmajrfg">
            <Field label="Onderwerp" count={{
            value: item.subject.length,
            max: MAIL_SUBJECT_MAX
          }} data-id="element-2717" data-mp="6wqx0j0pfv14">
              <input value={item.subject} onChange={e => onChange({
              subject: e.target.value
            })} placeholder={item.standard.subject} className={inputCls} data-id="element-2718" data-mp="1zs7kdrzkc99" />
            </Field>
            <Field label="Voorvertoningsregel" count={{
            value: item.preheader.length,
            max: MAIL_PREHEADER_MAX
          }} hint="De grijze regel achter het onderwerp." data-id="element-2719" data-mp="36dguftvvcr3o">
              <input value={item.preheader} onChange={e => onChange({
              preheader: e.target.value
            })} placeholder={item.standard.preheader} className={inputCls} data-id="element-2720" data-mp="3k6euhtj9b7dk" />
            </Field>
          </div>
        </Panel>

        <Panel title="Tekst" description="Een witregel begint een nieuwe alinea. Opmaak, logo en voettekst komen uit Huisstijl & afzender." data-id="element-2721" data-mp="2rlog9l7t9j3j">
          <ol className="space-y-5" data-id="element-2722" data-mp="125ujmuxfzkay">
            {item.blocks.map((b, i) => <li key={b.id} className="space-y-3 border-b border-line pb-5 last:border-0 last:pb-0" data-id="element-2723" data-mp="16y5jxyw3h841" data-mpi={i}>
                <div className="flex items-center justify-between gap-3" data-id="element-2724" data-mp="t6qxfi2zaos8">
                  <span className="text-[13px] font-medium text-ink" data-id="element-2725" data-mp="y5bpgu82u25l">Blok {i + 1}</span>
                  {item.blocks.length > 1 && <button type="button" onClick={() => onChange({
                blocks: item.blocks.filter((_, j) => j !== i)
              })} className={btnIcon} aria-label={`Blok ${i + 1} verwijderen`} data-id="element-2726" data-mp="1lvxx4ke65kf2">
                      <Trash2Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-2727" data-mp="1gv0gwdcpz0g7" />
                    </button>}
                </div>
                <Field label="Tussenkop" hint="Mag leeg." data-id="element-2728" data-mp="jwg2u1gk5iz">
                  <input value={b.heading} onChange={e => setBlock(i, {
                heading: e.target.value
              })} className={inputCls} data-id="element-2729" data-mp="5hftjrqc0e9v" />
                </Field>
                <Field label="Tekst" data-id="element-2730" data-mp="3dib14qhpn6p2">
                  <textarea value={b.body} onChange={e => setBlock(i, {
                body: e.target.value
              })} rows={6} className={`${inputCls} resize-y leading-relaxed`} data-id="element-2731" data-mp="3djdtz3ylfj7m" />
                </Field>
              </li>)}
          </ol>
          <div className="mt-5 flex flex-wrap gap-2 border-t border-line pt-4" data-id="element-2732" data-mp="33s9f048x3ycs">
            <button type="button" onClick={addBlock} className={btnGhost} data-id="element-2733" data-mp="2yu7v5h3o3v09">
              <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-2734" data-mp="2tu2a4jrwl6r5" />
              Blok toevoegen
            </button>
            <button type="button" onClick={() => {
            const {
              subject,
              preheader,
              blocks
            } = withStandardText(item);
            onChange({
              subject,
              preheader,
              blocks
            });
          }} disabled={standard} className={btnGhost} data-id="element-2735" data-mp="2pt9yjjvk6prp">
              <RotateCcwIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-2736" data-mp="j07jdhe7088m" />
              Terug naar standaardtekst
            </button>
          </div>
          {item.button && <div className="mt-5 border-t border-line pt-5" data-id="element-2737" data-mp="vj1rbjxyid9v">
              <Field label="Tekst op de knop" hint="De link vult de server zelf in; die kun je niet aanpassen." data-id="element-2738" data-mp="10cnw6pxvpcv4">
                <input value={item.button.label} onChange={e => item.button && onChange({
              button: {
                ...item.button,
                label: e.target.value
              }
            })} className={inputCls} data-id="element-2739" data-mp="159eeljx5okjp" />
              </Field>
            </div>}
        </Panel>

        <Panel title="Invulvelden" description="Deze vult de server per klant in. Klik om er een te kopiëren." data-id="element-2740" data-mp="hjk0z050irye">
          <dl className="divide-y divide-line" data-id="element-2741" data-mp="lo1jyx3jt184">
            {item.variables.map(v => <div key={v.key} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2.5 first:pt-0 last:pb-0" data-id="element-2742" data-mp="1szqw9mi7dvnh">
                <dt data-id="element-2743" data-mp="1x3ygp7hmopfw">
                  <button type="button" onClick={() => copyVariable(v.key)} className="min-h-[40px] rounded-md bg-sand px-2 font-mono text-[13px] text-ink transition-colors duration-150 ease-soft hover:bg-blush" data-id="element-2744" data-mp="2j35v55j2n890">
                    {`{${v.key}}`}
                  </button>
                </dt>
                <dd className="min-w-0 flex-1 text-[13px] text-muted" data-id="element-2745" data-mp="2o0fimfmwh9u7">
                  {v.label} <span className="text-ink/70" data-id="element-2746" data-mp="31s2eiecxkgcg">· bijv. “{v.example}”</span>
                </dd>
              </div>)}
          </dl>
        </Panel>
      </div>

      <div className="min-w-0 lg:col-span-5" data-id="element-2747" data-mp="3br1pi3l64o2m">
        <div className="space-y-6 lg:sticky lg:top-20" data-id="element-2748" data-mp="3bq0ajo117hfm">
          <Panel title="Klopt alles?" data-id="element-2749" data-mp="36u1klw021rd3">
            <CheckList checks={checks} okText="Deze mail is in orde." data-id="element-2750" data-mp="36qxakhzws1ei" />
            <div className="mt-4 border-t border-line pt-4" data-id="element-2751" data-mp="1helmrcrkzs2i">
              <button type="button" onClick={onTest} disabled={dirty} aria-describedby={dirty ? `test-${item.key}` : undefined} className={btnSecondary} data-id="element-2752" data-mp="1cfiezkk7v7qu">
                <SendIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-2753" data-mp="3o4f7slmi34ve" />
                Stuur testmail naar mij
              </button>
              {dirty && <p id={`test-${item.key}`} className="mt-2 text-[12px] text-muted" data-id="element-2754" data-mp="3ebpynxefa338">
                  Sla eerst op: de testmail gebruikt de opgeslagen tekst.
                </p>}
            </div>
          </Panel>
          <NewsletterPreview html={preview.html} subject={preview.subject} preheader={preview.preheader} from={from} data-id="element-2755" data-mp="35dxjqucm4xgd" />
        </div>
      </div>
    </div>;
}